import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';

import {getPercent} from '@ey/utils';

import {dashboardChartColors, dashboardTabId, numberFormatsListItems} from '../../../../util/uiconstants';
import {labels} from '../../../../util/utils';
import Utility from '../../../../util/utilityfunctions';

import ReviewNotes from './Charts/ReviewNotes';
import ProjectManagementEngagementTable from './Charts/ProjectManagementEngagementTable';
import ReviewNotesByPriorityChart from './Charts/ReviewNotesByPriorityChart';
import TaskAssignmentChart from './Charts/TaskAssignmentChart';

import normalizeDashboardFilters from '../../../../util/dashboard/normalizeDashboardFilters';
import {
	clearFilterDashboardTiles,
	updateFilterDashboardTiles
} from '../../../../actions/Metrics/dashboardFiltersAction';

const ProjectManagement = ({engagementMetrics}) => {
	const dispatch = useDispatch();
	const userPreferences = useSelector((store) => store.userPreferences);
	const preferredNumberFormat = userPreferences.numberFormat;

	const initialStateAssignmentData = {
		totalAssignedTask: 0,
		totalUnassignedTask: 0,
		openTask: 0,
		openOverdueTask: 0,
		inProgressTask: 0,
		inProgressOverdueTask: 0,
		completedTask: 0
	};

	/**
	 * @constant
	 * @type {Object} Each key represents one graphic of this tab
	 */
	const graphics = {
		preparerAssignments: 'assignments-preparer',
		reviewerAssignments: 'assignments-reviewer',
		allReviewNotes: 'notes-status',
		openReviewNotes: 'notes-priority'
	};

	const [reviewNotesData, setReviewNotesData] = useState([]);
	const [preparerAssignmentData, setPreparerAssignmentData] = useState(initialStateAssignmentData);
	const [reviewerAssignmentData, setReviewerAssignmentData] = useState(initialStateAssignmentData);

	// on user preferences update, clean this tab filters
	useEffect(() => {
		handlerOnClearFilter();
	}, [userPreferences]);

	useEffect(() => {
		let data = [];
		let preparerAssignment = {...initialStateAssignmentData};
		let reviewerAssignment = {...initialStateAssignmentData};
		if (engagementMetrics.length > 0) {
			engagementMetrics.forEach((item) => {
				data.push({
					engKey: item.engKey,
					engId: item.engId,
					rNTotal: item.rNTotal,
					rNO: item.rNO,
					rNOLow: item.rNOLow,
					rNOMed: item.rNOMed,
					rNOHigh: item.rNOHigh,
					rNOCrit: item.rNOCrit,
					rNOLowOD: item.rNOLowOD,
					rNOMedOD: item.rNOMedOD,
					rNOHighOD: item.rNOHighOD,
					rNOCritOD: item.rNOCritOD,
					rNCLow: item.rNCLow,
					rNCMed: item.rNCMed,
					rNCHigh: item.rNCHigh,
					rNCCrit: item.rNCCrit,
					rNCloMed: item.rNCloMed,
					rNCloLow: item.rNCloLow,
					rNCloCrit: item.rNCloCrit,
					rNCloHigh: item.rNCloHigh,
					rNTotalCalculatedOpen: (item.rNOLow || 0) + (item.rNOMed || 0) + (item.rNOHigh || 0) + (item.rNOCrit || 0),
					rNAllCalculatedCount:
						(item.rNO || 0) +
						(item.rNCLow || 0) +
						(item.rNCMed || 0) +
						(item.rNCHigh || 0) +
						(item.rNCCrit || 0) +
						(item.rNCloMed || 0) +
						(item.rNCloLow || 0) +
						(item.rNCloCrit || 0) +
						(item.rNCloHigh || 0)
				});
				// Update count for preparer assignment
				preparerAssignment.totalAssignedTask = preparerAssignment.totalAssignedTask + item.pATot;
				preparerAssignment.totalUnassignedTask = preparerAssignment.totalUnassignedTask + item.pAUnAsgn;
				preparerAssignment.openTask = preparerAssignment.openTask + item.pAOpen;
				preparerAssignment.openOverdueTask = preparerAssignment.openOverdueTask + item.pAOpenOD;
				preparerAssignment.inProgressTask = preparerAssignment.inProgressTask + item.pAInProg;
				preparerAssignment.inProgressOverdueTask = preparerAssignment.inProgressOverdueTask + item.pAInProgOD;
				preparerAssignment.completedTask = preparerAssignment.completedTask + item.pACompld;

				reviewerAssignment.totalAssignedTask = reviewerAssignment.totalAssignedTask + item.rA;
				reviewerAssignment.totalUnassignedTask = reviewerAssignment.totalUnassignedTask + item.rAUnAsgn;
				reviewerAssignment.openTask = reviewerAssignment.openTask + item.rANotStart;
				reviewerAssignment.openOverdueTask = reviewerAssignment.openOverdueTask + item.rANotStartOD;
				reviewerAssignment.inProgressTask = reviewerAssignment.inProgressTask + item.rAInProg;
				reviewerAssignment.inProgressOverdueTask = reviewerAssignment.inProgressOverdueTask + item.rAInProgOD;
				reviewerAssignment.completedTask = reviewerAssignment.completedTask + item.rACompld;
			});
		}
		setReviewNotesData(data);
		setPreparerAssignmentData(preparerAssignment);
		setReviewerAssignmentData(reviewerAssignment);
	}, [engagementMetrics]);

	const getChartData = (assignmentData) => {
		const numberFormat = numberFormatsListItems.find((x) => x.label == preferredNumberFormat);
		const getFormattedOpenOverdueTaskCountText = `${Utility.getFormattedNumber(preferredNumberFormat, assignmentData.openOverdueTask)} ${labels.overdue}`;
		const getFormattedInProgressOverdueTaskCountText = `${Utility.getFormattedNumber(preferredNumberFormat, assignmentData.inProgressOverdueTask)} ${labels.overdue}`;
		const barChartData = {
			barStyling: {
				chartHeight: 180,
				styleName: 'preparer-assignments',
				chartWrapper: ''
			},
			barMaxValue: assignmentData.totalAssignedTask,
			numberFormat: numberFormat && numberFormat.id,
			showBarTitleTip: true,
			barItems: [
				{
					count: assignmentData.openTask,
					label: labels.open,
					statusClass: 'motif-body2-default-regular',
					color: dashboardChartColors.blue[0],
					warningMessage: (
						<span className="motif-body3-default-light" title={getFormattedOpenOverdueTaskCountText}>
							{assignmentData.openOverdueTask > 0 ? getFormattedOpenOverdueTaskCountText : ''}
						</span>
					),
					toolTip: `${labels.open} | ${Utility.getFormattedNumber(
						preferredNumberFormat,
						assignmentData.openTask
					)} (${getPercent(assignmentData.openTask, assignmentData.totalAssignedTask)}%)`
				},
				{
					count: assignmentData.inProgressTask,
					label: labels.inprogress,
					statusClass: 'motif-body2-default-regular',
					color: dashboardChartColors.blue[2],
					warningMessage: (
						<span className="motif-body3-default-light" title={getFormattedInProgressOverdueTaskCountText}>
							{assignmentData.inProgressOverdueTask > 0 ? getFormattedInProgressOverdueTaskCountText : ''}
						</span>
					),
					toolTip: `${labels.inprogress} | ${Utility.getFormattedNumber(
						preferredNumberFormat,
						assignmentData.inProgressTask
					)} (${getPercent(assignmentData.inProgressTask, assignmentData.totalAssignedTask)}%)`
				},
				{
					count: assignmentData.completedTask,
					label: labels.closed,
					statusClass: 'motif-body2-default-regular',
					color: dashboardChartColors.blue[3],
					warningMessage: '',
					toolTip: `${labels.closed} | ${Utility.getFormattedNumber(
						preferredNumberFormat,
						assignmentData.completedTask
					)} (${getPercent(assignmentData.completedTask, assignmentData.totalAssignedTask)}%)`
				}
			]
		};
		return barChartData;
	};

	/**
	 * @param {string} serieLabel - Label to be displayed inside the filter chip
	 * @param {string} graphicsKey - Key that represent one of this tab graphics
	 * @see {@link graphics} to see graphicsKey values
	 */
	const onGraphicClickHandler = (serieLabel, graphicsKey) => {
		if (
			graphicsKey === graphics.allReviewNotes &&
			(serieLabel?.includes(labels.cleared) || serieLabel?.includes(labels.closed))
		) {
			return;
		}

		updateFilters(graphicsKey, serieLabel);
	};

	/**
	 * @param {string} graphicsKey - Key that represent one of this tab graphics
	 * @see {@link graphics} to see graphicsKey values
	 */
	const updateUnassignedFilterHandler = (graphicsKey) => {
		const serie = graphicsKey == graphics.preparerAssignments ? labels.preparerNotAssigned : labels.reviewerNotAssigned;
		updateFilters(graphicsKey, serie);
	};

	const handlerOnClearFilter = () => dispatch(clearFilterDashboardTiles(dashboardTabId.projectManagement));

	/**
	 * @description Map & dispatch the filters of projectManagement Dashboard Tiles.
	 * @param {string} serieLabel - Label to be displayed inside the filter chip
	 * @param {string} graphicsKey - Key that represent one of this tab graphics
	 * @see {@link graphics} to see graphicsKey values
	 */
	const updateFilters = (graphicsKey, serieLabel) => {
		const filter = [
			{categoryLabel: getSelectedCategory(graphicsKey), serieLabel: serieLabel, onClearChip: handlerOnClearFilter}
		];

		dispatch(
			updateFilterDashboardTiles(
				dashboardTabId.projectManagement,
				normalizeDashboardFilters(
					filter,
					engagementMetrics?.filter((eng) => filterEngagementByChips(eng, filter)).map((e) => e.engId) ?? [],
					handlerOnClearFilter
				)
			)
		);
	};

	const filterEngagementByChips = (engagement, filter) => {
		if (!engagement || _.isEmpty(engagement)) return false;

		return filter.some(({categoryLabel, serieLabel}) => {
			switch (categoryLabel) {
				case labels.preparerAssignments:
					return evalPreparerAssigments(engagement, serieLabel);
				case labels.reviewerAssignments:
					return evalReviewerAssigments(engagement, serieLabel);
				case labels.reviewNotesByStatusTitle:
					return evalNotesByStatus(engagement, serieLabel);
				case labels.openReviewNotes:
					return evalNotesByPriority(engagement, serieLabel);
				default:
					return false;
			}
		});
	};

	const evalPreparerAssigments = (engagement, serieLabel) => {
		const {pAOpen, pAInProg, pACompld, pAUnAsgn, pAOpenOD, pAInProgOD} = engagement;
		switch (serieLabel) {
			case labels.open:
				return pAOpen;
			case labels.inprogress:
				return pAInProg;
			case labels.closed:
				return pACompld;
			case labels.preparerNotAssigned:
				return pAUnAsgn;
			case `${labels.open} ${labels.overdue}`:
				return pAOpenOD;
			case `${labels.inprogress} ${labels.overdue}`:
				return pAInProgOD;
		}
	};

	const evalReviewerAssigments = (engagement, serieLabel) => {
		const {rANotStart, rAInProg, rACompld, rAUnAsgn, rANotStartOD, rAInProgOD} = engagement;
		switch (serieLabel) {
			case labels.open:
				return rANotStart;
			case labels.inprogress:
				return rAInProg;
			case labels.closed:
				return rACompld;
			case labels.reviewerNotAssigned:
				return rAUnAsgn;
			case `${labels.open} ${labels.overdue}`:
				return rANotStartOD;
			case `${labels.inprogress} ${labels.overdue}`:
				return rAInProgOD;
		}
	};

	const evalNotesByStatus = (engagement, serieLabel) => {
		const {
			rNO, //Open
			rNCLow,
			rNCMed,
			rNCHigh,
			rNCCrit, // Cleared
			rNCloLow,
			rNCloMed,
			rNCloHigh,
			rNCloCrit, // Closed
			rNOLowOD,
			rNOMedOD,
			rNOHighOD,
			rNOCritOD // Open Overdue
		} = engagement;
		switch (serieLabel) {
			case labels.open:
				return rNO;
			case labels.cleared:
				return rNCLow || rNCMed || rNCHigh || rNCCrit;
			case labels.closed:
				return rNCloLow || rNCloMed || rNCloHigh || rNCloCrit;
			case `${labels.open} ${labels.overdue}`:
				return rNOLowOD || rNOMedOD || rNOHighOD || rNOCritOD;
		}
	};

	const evalNotesByPriority = (engagement, serieLabel) => {
		const {
			rNOLow,
			rNOLowOD, // Low
			rNOMed,
			rNOMedOD, //Medium
			rNOHigh,
			rNOHighOD, // High
			rNOCrit,
			rNOCritOD // Critical
		} = engagement;
		switch (serieLabel) {
			case labels.low:
				return rNOLow || rNOLowOD;
			case labels.medium:
				return rNOMed || rNOMedOD;
			case labels.high:
				return rNOHigh || rNOHighOD;
			case labels.critical:
				return rNOCrit || rNOCritOD;
		}
	};

	/**
	 * @param {string} graphicsKey - Key that represent one of this tab graphics
	 * @see {@link graphics} to see graphicsKey values
	 * @returns the label to be displayed at the category of the filter chip
	 */
	const getSelectedCategory = (graphicsKey) => {
		switch (graphicsKey) {
			case graphics.allReviewNotes:
				return labels.reviewNotesByStatusTitle;
			case graphics.openReviewNotes:
				return labels.openReviewNotes;
			case graphics.preparerAssignments:
				return labels.preparerAssignments;
			case graphics.reviewerAssignments:
				return labels.reviewerAssignments;
			default:
				return '';
		}
	};

	return (
		<>
			<TaskAssignmentChart
				tileHeading={labels.preparerAssignments}
				tileSubHeading={labels.byAssignmentStatus}
				assignedCount={Utility.getFormattedNumber(preferredNumberFormat, preparerAssignmentData.totalAssignedTask)}
				assignedLabel={labels.totalPreparerAssignments}
				isDataAvailable={preparerAssignmentData.totalAssignedTask > 0 || preparerAssignmentData.totalUnassignedTask > 0}
				noDataAvailableMessage={labels.noResultMessageHeader}
				barChartData={getChartData(preparerAssignmentData)}
				unassignedCount={Utility.getFormattedNumber(preferredNumberFormat, preparerAssignmentData.totalUnassignedTask)}
				unassignedLabel={
					preparerAssignmentData.totalUnassignedTask == 1
						? labels.taskWithoutPreparerAssignments
						: labels.tasksWithoutPreparerAssignments
				}
				unassignedLabelClickHandler={() => updateUnassignedFilterHandler(graphics.preparerAssignments)}
				onGraphicClick={onGraphicClickHandler}
				filterKey={graphics.preparerAssignments}
			/>
			<TaskAssignmentChart
				tileHeading={labels.reviewerAssignments}
				tileSubHeading={labels.byAssignmentStatus}
				assignedCount={Utility.getFormattedNumber(preferredNumberFormat, reviewerAssignmentData.totalAssignedTask)}
				assignedLabel={labels.totalReviewerAssignments}
				isDataAvailable={reviewerAssignmentData.totalAssignedTask > 0 || reviewerAssignmentData.totalUnassignedTask > 0}
				noDataAvailableMessage={labels.noResultMessageHeader}
				barChartData={getChartData(reviewerAssignmentData)}
				unassignedCount={Utility.getFormattedNumber(preferredNumberFormat, reviewerAssignmentData.totalUnassignedTask)}
				unassignedLabel={
					reviewerAssignmentData.totalUnassignedTask == 1
						? labels.taskWithoutReviewerAssignments
						: labels.tasksWithoutReviewerAssignments
				}
				unassignedLabelClickHandler={() => updateUnassignedFilterHandler(graphics.reviewerAssignments)}
				onGraphicClick={onGraphicClickHandler}
				filterKey={graphics.reviewerAssignments}
			/>
			<ReviewNotes
				reviewnotesData={reviewNotesData}
				onGraphicClick={onGraphicClickHandler}
				filterKey={graphics.allReviewNotes}
			/>
			<ReviewNotesByPriorityChart
				reviewnotesData={reviewNotesData}
				onGraphicClick={onGraphicClickHandler}
				filterKey={graphics.openReviewNotes}
			/>
			<ProjectManagementEngagementTable engagementMetrics={engagementMetrics} />
		</>
	);
};

export default ProjectManagement;
